import React, { useState, useRef, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  HStack,
  Box,
  Text,
  Divider,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Icon,
  Flex,
  Badge,
  useToast,
  Select,
  IconButton,
  Spinner
} from '@chakra-ui/react';
import { FaFileInvoice, FaUser, FaMapMarkerAlt, FaBriefcase, FaClock, FaDollarSign, FaSignature, FaEraser, FaPen, FaCog, FaPlus, FaTrash } from 'react-icons/fa';
import Client from '../../helpers/Client';
import urlToId from '../../helpers/UrlToId';

// Dijital İmza Bileşeni
const DigitalSignaturePad = ({ onSignatureChange, label, height = "150px" }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  const bgColor = 'white';
  const borderColor = 'gray.300';
  const textColor = 'gray.800';
  const mutedTextColor = 'gray.600';

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches[0].clientX) - rect.left;
    const y = (e.clientY || e.touches[0].clientY) - rect.top;

    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches[0].clientX) - rect.left;
    const y = (e.clientY || e.touches[0].clientY) - rect.top;

    const ctx = canvas.getContext('2d');
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#2D3748';
    ctx.lineTo(x, y);
    ctx.stroke();

    setHasSignature(true);
    if (onSignatureChange) {
      onSignatureChange(canvas.toDataURL());
    }
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    if (onSignatureChange) {
      onSignatureChange('');
    }
  };

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
  }, [bgColor]);

  return (
    <VStack spacing={2} align="stretch">
      <Text fontSize="sm" fontWeight="semibold" color={textColor}>
        {label}
      </Text>
      <Box
        position="relative"
        border="2px solid"
        borderColor={borderColor}
        borderRadius="lg"
        bg={bgColor}
        overflow="hidden"
      >
        <canvas
          ref={canvasRef}
          width={300}
          height={120}
          style={{
            display: 'block',
            cursor: 'crosshair',
            width: '100%',
            height: height
          }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        {!hasSignature && (
          <Box
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            pointerEvents="none"
          >
            <VStack spacing={1}>
              <Icon as={FaPen} color={mutedTextColor} boxSize={4} />
              <Text color={mutedTextColor} fontSize="xs" textAlign="center">
                İmzalamak için çizin
              </Text>
            </VStack>
          </Box>
        )}
      </Box>
      <Button
        size="xs"
        variant="outline"
        colorScheme="red"
        leftIcon={<FaEraser />}
        onClick={clearSignature}
        isDisabled={!hasSignature}
      >
        Temizle
      </Button>
    </VStack>
  );
};

const InvoiceModal = ({ isOpen, onClose, ticket }) => {
  // Debug: Log ticket data when component renders
  useEffect(() => {
    console.log('InvoiceModal - ticket prop:', ticket);
    console.log('InvoiceModal - ticket.id:', ticket?.id);
    console.log('InvoiceModal - ticket.url:', ticket?.url);
    console.log('InvoiceModal - extracted ID:', ticket?.url ? urlToId(ticket.url) : 'no url');
    console.log('InvoiceModal - ticket keys:', ticket ? Object.keys(ticket) : 'no ticket');
    console.log('InvoiceModal - isOpen:', isOpen);

    // Eğer modal açık ama ticket yoksa hata göster
    if (isOpen && (!ticket || !ticket.url)) {
      console.error('InvoiceModal opened without valid ticket data:', { ticket, isOpen });
    }
  }, [ticket, isOpen]);

  // localStorage'dan fatura template bilgilerini al
  const getInvoiceTemplate = () => {
    try {
      const saved = localStorage.getItem('invoiceTemplate');
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      return {};
    }
  };

  const [invoiceTemplate, setInvoiceTemplate] = useState({});
  const [products, setProducts] = useState([]);
  const [invoiceData, setInvoiceData] = useState({
    customer_name: ticket?.full_name || '',
    customer_phone: ticket?.phone_number || '',
    customer_email: ticket?.email || '',
    unit_address: ticket?.unit ? `${ticket.unit.project?.name || ''} - ${ticket.unit.apt_num || ''}` : '',
    description: ticket?.body || '',
    service_fee: 0,
    items: [],
    customer_signature: '',
    dealer_signature: ''
  });

  // Modal açıldığında ticket verilerini güncelle
  useEffect(() => {
    if (isOpen && ticket) {
      setInvoiceData(prev => ({
        ...prev,
        customer_name: ticket?.full_name || '',
        customer_phone: ticket?.phone_number || '',
        customer_email: ticket?.email || '',
        unit_address: ticket?.unit ? `${ticket.unit.project?.name || ''} - ${ticket.unit.apt_num || ''}` : '',
        description: ticket?.body || '',
      }));
    }
  }, [isOpen, ticket]);

  const [isLoading, setIsLoading] = useState(false);
  const [isCreatingInvoice, setIsCreatingInvoice] = useState(false);
  const toast = useToast();

  // Eğer modal açık ama ticket bilgisi yoksa, modal'ı kapat ve hata göster
  useEffect(() => {
    if (isOpen && (!ticket || !ticket.url)) {
      toast({
        title: 'Hata',
        description: 'Ticket bilgisi bulunamadı. Lütfen sayfayı yenileyin.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      onClose(); // Modal'ı kapat
    }
  }, [isOpen, ticket, toast, onClose]);

  // Modal açıldığında template bilgilerini ve ürünleri yükle
  useEffect(() => {
    if (isOpen && ticket?.url) {
      const templateData = getInvoiceTemplate();
      setInvoiceTemplate(templateData);
      loadProducts();

      // Ticket'tan gelen service records'ları invoice items olarak ekle
      if (ticket?.service_records) {
        const items = ticket.service_records.map(record => ({
          product: record.product?.id || '',
          description: record.product?.model?.name || record.product?.code || '',
          quantity: 1,
          unit_price: 0
        }));
        setInvoiceData(prev => ({ ...prev, items }));
      }
    }
  }, [isOpen, ticket?.url]);

  const loadProducts = async () => {
    setIsLoading(true);
    try {
      const response = await Client.get('/models/');
      const productsData = response.data.results || response.data || [];
      // Ensure all products have valid id and structure
      const validProducts = productsData.filter(product => product && product.id);
      setProducts(validProducts);
    } catch (error) {
      console.error('Error loading products:', error);
      setProducts([]); // Set empty array on error
      toast({
        title: 'Hata',
        description: 'Ürünler yüklenirken bir hata oluştu.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Color mode values
  const bgColor = 'white';
  const borderColor = 'gray.200';
  const textColor = 'gray.800';
  const mutedTextColor = 'gray.600';
  const headerBg = 'blue.50';
  const signatureBoxBg = 'gray.50';

  const totalAmount = invoiceData.service_fee + invoiceData.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);

  const handleInputChange = (field, value) => {
    setInvoiceData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleItemChange = (index, field, value) => {
    setInvoiceData(prev => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const addItem = () => {
    setInvoiceData(prev => ({
      ...prev,
      items: [...prev.items, { part_name: '', description: '', quantity: 1, unit_price: 0 }]
    }));
  };

  const removeItem = (index) => {
    setInvoiceData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleSignatureChange = (field, signatureData) => {
    setInvoiceData(prev => ({
      ...prev,
      [field]: signatureData
    }));
  };

  const handleCreateInvoice = async () => {
    if (!invoiceData.customer_name || !invoiceData.description) {
      toast({
        title: 'Eksik Bilgi',
        description: 'Lütfen müşteri adı ve açıklama alanlarını doldurun.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!invoiceData.customer_signature || !invoiceData.dealer_signature) {
      toast({
        title: 'İmza Eksik',
        description: 'Lütfen hem müşteri hem de bayi imzasını tamamlayın.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!ticket || !ticket.url) {
      toast({
        title: 'Hata',
        description: 'Ticket bilgisi bulunamadı. Lütfen sayfayı yenileyin.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsCreatingInvoice(true);

    try {
      // Backend API'ye fatura oluşturma isteği gönder
      const ticketId = urlToId(ticket.url);
      const response = await Client.post(`/tickets/${ticketId}/create_invoice/`, invoiceData);

      toast({
        title: 'Fatura Oluşturuldu',
        description: 'Fatura başarıyla oluşturuldu.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      onClose();
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast({
        title: 'Hata',
        description: error.response?.data?.message || 'Fatura oluşturulurken bir hata oluştu.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsCreatingInvoice(false);
    }
  };

  const generateAndDownloadInvoicePDF = () => {
    const currentDate = new Date().toLocaleDateString('tr-TR');
    const invoiceNumber = `INV-${Date.now()}`;

    // Fatura HTML içeriği oluştur (Site ayarlarından template bilgilerini kullan)
    const invoiceHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Fatura</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; color: #333; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #007bff; padding-bottom: 20px; }
          .company-info { margin-bottom: 20px; background: #f8f9fa; padding: 15px; border-radius: 5px; }
          .invoice-info { margin-bottom: 20px; }
          .customer-info { margin-bottom: 20px; }
          .items-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
          .items-table th, .items-table td { border: 1px solid #ddd; padding: 12px; text-align: left; }
          .items-table th { background-color: #007bff; color: white; }
          .total { text-align: right; font-weight: bold; font-size: 18px; background: #f8f9fa; padding: 15px; border-radius: 5px; }
          .signature { margin-top: 30px; }
          .signature img { max-width: 200px; border: 1px solid #ddd; margin-top: 10px; }
          .signature div { margin-bottom: 20px; }
          .footer { margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; font-size: 12px; color: #666; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>FATURA</h1>
          <p>Fatura No: ${invoiceNumber}</p>
          <p>Tarih: ${currentDate}</p>
        </div>
        
        <div class="company-info">
          <h3>Şirket Bilgileri:</h3>
          <p><strong>Şirket Adı:</strong> ${invoiceTemplate.companyName || 'Şirket Adı Belirtilmemiş'}</p>
          <p><strong>Adres:</strong> ${invoiceTemplate.companyAddress || 'Adres Belirtilmemiş'}</p>
          <p><strong>Telefon:</strong> ${invoiceTemplate.companyPhone || 'Telefon Belirtilmemiş'}</p>
          <p><strong>E-posta:</strong> ${invoiceTemplate.companyEmail || 'E-posta Belirtilmemiş'}</p>
          ${invoiceTemplate.taxNumber ? `<p><strong>Vergi No:</strong> ${invoiceTemplate.taxNumber}</p>` : ''}
        </div>
        
        <div class="customer-info">
          <h3>Müşteri Bilgileri:</h3>
          <p><strong>Ad Soyad:</strong> ${invoiceData.customerName}</p>
          <p><strong>Adres:</strong> ${invoiceData.customerAddress}</p>
        </div>
        
        <div class="invoice-info">
          <h3>Fatura Oluşturan:</h3>
          <p><strong>Ad Soyad:</strong> ${invoiceData.createdBy}</p>
        </div>
        
        <table class="items-table">
          <thead>
            <tr>
              <th>Açıklama</th>
              <th>Çalışma Saati</th>
              <th>Saatlik Ücret</th>
              <th>Toplam</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>${invoiceData.workDescription}</td>
              <td>${invoiceData.workHours} saat</td>
              <td>${invoiceData.hourlyRate} ₺</td>
              <td>${totalAmount} ₺</td>
            </tr>
          </tbody>
        </table>
        
        <div class="total">
          <p>Genel Toplam: ${totalAmount} ₺</p>
        </div>
        
        <div class="signature">
          <div style="margin-bottom: 30px;">
            <h4>Müşteri İmzası:</h4>
            <img src="${invoiceData.customerSignature}" alt="Müşteri İmzası" />
          </div>
          <div>
            <h4>Personel İmzası:</h4>
            <img src="${invoiceData.employeeSignature}" alt="Personel İmzası" />
          </div>
        </div>
        
        ${invoiceTemplate.invoiceFooter || invoiceTemplate.invoiceTerms ? `
        <div class="footer">
          ${invoiceTemplate.invoiceTerms ? `<p><strong>Ödeme Şartları:</strong> ${invoiceTemplate.invoiceTerms}</p>` : ''}
          ${invoiceTemplate.invoiceFooter ? `<p>${invoiceTemplate.invoiceFooter}</p>` : ''}
        </div>
        ` : ''}
      </body>
      </html>
    `;

    // Blob oluştur ve indir
    const blob = new Blob([invoiceHTML], { type: 'text/html' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `fatura-${invoiceNumber}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
      <ModalContent bg={bgColor} borderRadius="xl" boxShadow="2xl">
        <ModalHeader
          bg={headerBg}
          borderTopRadius="xl"
          borderBottom="1px solid"
          borderColor={borderColor}
        >
          <HStack spacing={3}>
            <Icon as={FaFileInvoice} color="blue.500" boxSize={6} />
            <Text fontSize="xl" fontWeight="bold" color={textColor}>
              Fatura Oluştur
            </Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody py={6}>
          <VStack spacing={6} align="stretch">
            {/* Müşteri Bilgileri */}
            <Box>
              <Text fontSize="lg" fontWeight="bold" color={textColor} mb={4}>
                Müşteri Bilgileri
              </Text>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel color={textColor} fontWeight="semibold">
                    <HStack spacing={2}>
                      <Icon as={FaUser} color="green.500" />
                      <Text>Müşteri Adı Soyadı</Text>
                    </HStack>
                  </FormLabel>
                  <Input
                    value={invoiceData.customer_name}
                    onChange={(e) => handleInputChange('customer_name', e.target.value)}
                    placeholder="Müşteri adı ve soyadı"
                    bg={bgColor}
                    borderColor={borderColor}
                    _hover={{ borderColor: 'blue.300' }}
                    _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px #3182ce' }}
                  />
                </FormControl>

                <HStack spacing={4} w="full">
                  <FormControl>
                    <FormLabel color={textColor} fontWeight="semibold">Telefon</FormLabel>
                    <Input
                      value={invoiceData.customer_phone}
                      onChange={(e) => handleInputChange('customer_phone', e.target.value)}
                      placeholder="Müşteri telefonu"
                      bg={bgColor}
                      borderColor={borderColor}
                      _hover={{ borderColor: 'blue.300' }}
                      _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px #3182ce' }}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel color={textColor} fontWeight="semibold">E-posta</FormLabel>
                    <Input
                      value={invoiceData.customer_email}
                      onChange={(e) => handleInputChange('customer_email', e.target.value)}
                      placeholder="Müşteri e-postası"
                      bg={bgColor}
                      borderColor={borderColor}
                      _hover={{ borderColor: 'blue.300' }}
                      _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px #3182ce' }}
                    />
                  </FormControl>
                </HStack>

                <FormControl>
                  <FormLabel color={textColor} fontWeight="semibold">
                    <HStack spacing={2}>
                      <Icon as={FaMapMarkerAlt} color="red.500" />
                      <Text>Birim Adresi</Text>
                    </HStack>
                  </FormLabel>
                  <Input
                    value={invoiceData.unit_address}
                    onChange={(e) => handleInputChange('unit_address', e.target.value)}
                    placeholder="Birim adresi"
                    bg={bgColor}
                    borderColor={borderColor}
                    _hover={{ borderColor: 'blue.300' }}
                    _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px #3182ce' }}
                  />
                </FormControl>
              </VStack>
            </Box>

            <Divider />

            {/* İş Açıklaması */}
            <FormControl isRequired>
              <FormLabel color={textColor} fontWeight="semibold">
                <HStack spacing={2}>
                  <Icon as={FaBriefcase} color="purple.500" />
                  <Text>İş Açıklaması</Text>
                </HStack>
              </FormLabel>
              <Textarea
                value={invoiceData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Yapılan işin detaylı açıklaması"
                bg={bgColor}
                borderColor={borderColor}
                _hover={{ borderColor: 'blue.300' }}
                _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px #3182ce' }}
                resize="vertical"
                minH="100px"
              />
            </FormControl>

            <Divider />

            {/* Servis Ücreti */}
            <FormControl>
              <FormLabel color={textColor} fontWeight="semibold">
                <HStack spacing={2}>
                  <Icon as={FaDollarSign} color="green.500" />
                  <Text>Servis Ücreti (₺)</Text>
                </HStack>
              </FormLabel>
              <NumberInput
                value={invoiceData.service_fee}
                onChange={(value) => handleInputChange('service_fee', parseFloat(value) || 0)}
                min={0}
                precision={2}
              >
                <NumberInputField
                  bg={bgColor}
                  borderColor={borderColor}
                  _hover={{ borderColor: 'blue.300' }}
                  _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px #3182ce' }}
                />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>

            <Divider />

            {/* Ürün Kalemleri */}
            <Box>
              <HStack justify="space-between" mb={4}>
                <Text fontSize="lg" fontWeight="bold" color={textColor}>
                  Ürün Kalemleri
                </Text>
                <Button
                  size="sm"
                  colorScheme="blue"
                  leftIcon={<FaPlus />}
                  onClick={addItem}
                >
                  Kalem Ekle
                </Button>
              </HStack>

              {isLoading ? (
                <Flex justify="center" py={4}>
                  <Spinner />
                </Flex>
              ) : (
                <VStack spacing={4} align="stretch">
                  {invoiceData.items.map((item, index) => (
                    <Box key={index} p={4} border="1px solid" borderColor={borderColor} borderRadius="md">
                      <HStack justify="space-between" mb={3}>
                        <Text fontWeight="semibold" color={textColor}>Kalem {index + 1}</Text>
                        <IconButton
                          size="sm"
                          colorScheme="red"
                          variant="ghost"
                          icon={<FaTrash />}
                          onClick={() => removeItem(index)}
                        />
                      </HStack>

                      <VStack spacing={3} align="stretch">
                        <FormControl isRequired>
                          <FormLabel fontSize="sm">Parça Adı</FormLabel>
                          <Input
                            value={item.part_name}
                            onChange={(e) => handleItemChange(index, 'part_name', e.target.value)}
                            placeholder="Parça adı"
                            bg={bgColor}
                            borderColor={borderColor}
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel fontSize="sm">Açıklama</FormLabel>
                          <Input
                            value={item.description}
                            onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                            placeholder="Ürün açıklaması"
                            bg={bgColor}
                            borderColor={borderColor}
                          />
                        </FormControl>

                        <HStack spacing={3}>
                          <FormControl>
                            <FormLabel fontSize="sm">Miktar</FormLabel>
                            <NumberInput
                              value={item.quantity}
                              onChange={(value) => handleItemChange(index, 'quantity', parseInt(value) || 1)}
                              min={1}
                            >
                              <NumberInputField bg={bgColor} borderColor={borderColor} />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          </FormControl>

                          <FormControl>
                            <FormLabel fontSize="sm">Birim Fiyat (₺)</FormLabel>
                            <NumberInput
                              value={item.unit_price}
                              onChange={(value) => handleItemChange(index, 'unit_price', parseFloat(value) || 0)}
                              min={0}
                              precision={2}
                            >
                              <NumberInputField bg={bgColor} borderColor={borderColor} />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          </FormControl>

                          <FormControl>
                            <FormLabel fontSize="sm">Toplam</FormLabel>
                            <Input
                              value={`₺${(item.quantity * item.unit_price).toFixed(2)}`}
                              isReadOnly
                              bg="gray.50"
                              borderColor={borderColor}
                            />
                          </FormControl>
                        </HStack>
                      </VStack>
                    </Box>
                  ))}
                </VStack>
              )}
            </Box>

            {/* Toplam Tutar */}
            <Box
              w="full"
              p={4}
              bg={headerBg}
              borderRadius="lg"
              border="1px solid"
              borderColor={borderColor}
            >
              <VStack spacing={2}>
                <HStack justify="space-between" w="full">
                  <Text color={textColor}>Servis Ücreti:</Text>
                  <Text fontWeight="semibold">₺{invoiceData.service_fee.toFixed(2)}</Text>
                </HStack>
                <HStack justify="space-between" w="full">
                  <Text color={textColor}>Ürün Toplamı:</Text>
                  <Text fontWeight="semibold">₺{invoiceData.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0).toFixed(2)}</Text>
                </HStack>
                <Divider />
                <HStack justify="space-between" w="full">
                  <Text fontSize="lg" fontWeight="bold" color={textColor}>
                    Genel Toplam:
                  </Text>
                  <Badge colorScheme="green" fontSize="lg" px={3} py={1} borderRadius="full">
                    ₺{totalAmount.toFixed(2)}
                  </Badge>
                </HStack>
              </VStack>
            </Box>

            <Divider />

            {/* Dijital İmza Bölümü */}
            <Box>
              <Text fontSize="lg" fontWeight="bold" color={textColor} mb={4}>
                <HStack spacing={2}>
                  <Icon as={FaSignature} color="blue.500" />
                  <Text>Dijital İmza Bölümü</Text>
                </HStack>
              </Text>
              <Text fontSize="sm" color={mutedTextColor} mb={4}>
                Lütfen aşağıdaki alanlara parmağınız veya mouse ile imzanızı atın.
              </Text>
              <VStack spacing={6} align="stretch">
                <Box>
                  <DigitalSignaturePad
                    label="Müşteri İmzası"
                    onSignatureChange={(signature) => handleSignatureChange('customer_signature', signature)}
                  />
                </Box>
                <Box>
                  <DigitalSignaturePad
                    label="Bayi İmzası"
                    onSignatureChange={(signature) => handleSignatureChange('dealer_signature', signature)}
                  />
                </Box>
              </VStack>
              <Box mt={4} p={3} bg={headerBg} borderRadius="md">
                <HStack spacing={2}>
                  <Icon as={FaSignature} color="blue.500" boxSize={4} />
                  <Text fontSize="sm" color={textColor} fontWeight="semibold">
                    İmza Durumu:
                  </Text>
                  <Badge
                    colorScheme={invoiceData.customer_signature ? "green" : "red"}
                    variant="subtle"
                  >
                    Müşteri: {invoiceData.customer_signature ? "✓ Tamamlandı" : "✗ Eksik"}
                  </Badge>
                  <Badge
                    colorScheme={invoiceData.dealer_signature ? "green" : "red"}
                    variant="subtle"
                  >
                    Bayi: {invoiceData.dealer_signature ? "✓ Tamamlandı" : "✗ Eksik"}
                  </Badge>
                </HStack>
              </Box>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor={borderColor}>
          <Button
            variant="outline"
            mr={3}
            onClick={() => window.open('/site-ayarlari/', '_blank')}
            leftIcon={<FaCog />}
            size="sm"
          >
            Template Ayarları
          </Button>
          <Button variant="ghost" mr={3} onClick={onClose}>
            İptal
          </Button>
          <Button
            colorScheme="green"
            onClick={handleCreateInvoice}
            isLoading={isCreatingInvoice}
            loadingText="Oluşturuluyor..."
            leftIcon={<FaFileInvoice />}
          >
            E-Fatura Oluştur
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default InvoiceModal;
